import { DatePicker } from '@commenda/kakashi';
import { useContext, useMemo } from 'react';
import { FormContext } from '../../hooks/FormContext';
import { isRuleActive } from '../../utils/validation';
import type { ValidationRules } from '@commenda/types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

/**
 * Validate and normalize a date value to ISO 8601 format (YYYY-MM-DD)
 * Returns undefined if the value is invalid
 */
function normalizeDate(value: unknown): string | undefined {
  if (!value) return undefined;
  
  // If it's not a string, it's invalid for DatePicker
  if (typeof value !== 'string') {
    console.warn('[DateInput] Received non-string date value:', value);
    return undefined;
  }
  
  // Check if it's a valid ISO 8601 date format (YYYY-MM-DD)
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDateRegex.test(value)) {
    console.warn('[DateInput] Invalid date format, expected YYYY-MM-DD:', value);
    return undefined;
  }
  
  return value;
}

export const DateInputComponent = ({
  fieldId,
  label,
  description,
  placeholder,
  props = {},
  guidance,
  validation,
}: {
  fieldId: string;
  label?: string;
  description?: string;
  placeholder?: string;
  props?: Record<string, unknown>;
  guidance?: { text?: string };
  validation?: {
    rules?: ValidationRules;
    triggers?: string[];
  };
}) => {
  const { formData, updateField, errors, validateField, isFieldVisible, isFieldDisabled } =
    useContext(FormContext);
  
  // Normalize the date value to ensure it's a valid ISO 8601 string
  const value = useMemo(() => normalizeDate(formData[fieldId]), [formData[fieldId]]);
  const disabled = (props.disabled as boolean) || isFieldDisabled(fieldId);
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const handleDateChange = (date: string | undefined) => {
    updateField(fieldId, date);
    if (validation?.rules) {
      validateField(fieldId, date, validation.rules);
    }
  };

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          description={description}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
      )}
      <DatePicker
        date={value}
        setDate={handleDateChange}
        placeholder={placeholder || 'Select date'}
        hasError={!!errors[fieldId]}
        disabled={disabled}
        showOutsideDays={props.showOutsideDays as boolean | undefined}
        showWeekNumber={props.showWeekNumber as boolean | undefined}
        disableInput={(props.disableInput as boolean) || disabled}
        hideCalendar={props.hideCalendar as boolean | undefined}
        validateDate={
          props.validateDate as ((date: string) => boolean) | undefined
        }
      />
      {errors[fieldId] && (
        <p className="txt-xs-regular text-text-error mt-1">{errors[fieldId]}</p>
      )}
    </div>
  );
};
